import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  useGetCourseBySlugQuery,
  useGetCourseResultQuery,
} from "@/features/api/courseApi";

import {
  User,
  Award,
  History,
  Loader2,
  BarChart3,
  Trophy,
  TrendingUp,
  AlertTriangle,
  Clock,
  RotateCcw,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

/* ================= COMPONENT ================= */

export default function CourseResult() {
  const { slug } = useParams();
  const navigate = useNavigate();

  /* ================= COURSE ================= */

  const { data: courseRes, isLoading: courseLoading } =
    useGetCourseBySlugQuery(slug);

  const course = courseRes?.course;
  const PASS_PERCENT = course?.passPercentage || 30;

  /* ================= RESULT ================= */

  const { data: rawResult, isLoading: resultLoading } =
    useGetCourseResultQuery(course?._id, {
      skip: !course?._id,
    });

  /* ================= NORMALIZE ================= */

  const { results, user } = useMemo(() => {
    try {
      const progress = rawResult?.progress || {};
      if (!progress) return { results: [], user: {} };

      const lessons = Array.isArray(progress.lessonsProgress)
        ? progress.lessonsProgress
        : [];

      return {
        results: lessons.map((l, i) => ({
          lessonId: l?.lessonId || `l-${i}`,
          score: Number(l?.score ?? 0),
          timeSpent: Number(l?.timeSpent ?? 0),
        })),
        user: progress?.user || {},
      };
    } catch {
      return { results: [], user: {} };
    }
  }, [rawResult]);

  /* ================= LESSON MAP ================= */

  const lessonMap = useMemo(() => {
    if (!course?.lessons) return {};
    return Object.fromEntries(
      (course?.lessons || []).map((l) => [l._id, l.title])
    );
  }, [course]);

  /* ================= STATS ================= */

  const stats = useMemo(() => {
    if (!results.length) {
      return {
        total: 0,
        best: 0,
        avg: 0,
        latest: 0,
        passed: false,
      };
    }

    const scores = results.map((r) => r.score);

    const avg =
      scores.reduce((a, b) => a + b, 0) / scores.length;

    return {
      total: results.length,
      best: Math.max(...scores),
      avg: Math.round(avg),
      latest: scores[scores.length - 1],
      passed: scores[scores.length - 1] >= PASS_PERCENT,
    };
  }, [results, PASS_PERCENT]);

  /* ================= TIME ================= */

  const totalTime = useMemo(
    () => results.reduce((sum, r) => sum + r.timeSpent, 0),
    [results]
  );

  const avgTime = useMemo(
    () => (results.length ? totalTime / results.length : 0),
    [totalTime, results]
  );

  /* ================= CHART ================= */

  const chartData = useMemo(() => {
    return results.map((r, i) => ({
      name: lessonMap[r.lessonId] || `Lesson ${i + 1}`,
      score: r.score,
    }));
  }, [results, lessonMap]);

  /* ================= WEAK ================= */

  const weak = useMemo(() => {
    return results
      .filter((r) => r.score < PASS_PERCENT)
      .map((r) => lessonMap[r.lessonId] || "Lesson")
      .slice(0, 5);
  }, [results, lessonMap]);

  /* ================= INSIGHTS ================= */

  const insights = useMemo(() => {
    const arr = [];

    if (!results.length) return arr;

    if (stats.latest < PASS_PERCENT) {
      arr.push("You need improvement to pass.");
    }

    if (stats.avg < 50) {
      arr.push("Focus on fundamentals.");
    }

    if (stats.avg >= 75) {
      arr.push("Excellent performance 🔥");
    }

    if (!arr.length) arr.push("Keep practicing 👍");

    return arr;
  }, [stats, PASS_PERCENT, results]);

  /* ================= STATES ================= */

  if (courseLoading || resultLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="animate-spin mr-2" />
        Loading...
      </div>
    );
  }

  if (!results.length) {
    return (
      <div className="text-center py-20">
        <p className="mb-4 text-lg">No results yet</p>
        <button
          onClick={() => navigate(`/courses/${slug}/learn`)}
          className="bg-blue-600 text-white px-6 py-2 rounded"
        >
          Start Learning
        </button>
      </div>
    );
  }

  /* ================= UI ================= */

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-6xl mx-auto space-y-8">

        {/* TITLE */}
        <h1 className="text-3xl font-bold text-center">
          {course?.title}
        </h1>

        {/* PASS STATUS */}
        <div className="text-center">
          {stats.passed ? (
            <div className="text-green-600 flex items-center justify-center gap-2">
              <CheckCircle2 /> Passed
            </div>
          ) : (
            <div className="text-red-600 flex items-center justify-center gap-2">
              <XCircle /> Not Passed
            </div>
          )}
        </div>

        {/* STATS */}
        <div className="grid md:grid-cols-5 gap-4">
          <Stat icon={Trophy} label="Best" value={`${stats.best}%`} />
          <Stat icon={BarChart3} label="Average" value={`${stats.avg}%`} />
          <Stat icon={History} label="Lessons" value={stats.total} />
          <Stat icon={Award} label="Latest" value={`${stats.latest}%`} />
          <Stat
            icon={Clock}
            label="Avg Time"
            value={`${Math.floor(avgTime / 60)}m`}
          />
        </div>

        {/* GRAPH */}
        <Card title="Progress" icon={TrendingUp}>
          <ResponsiveContainer width="100%" height={300}>
            {chartData.length > 0 && (
              <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="score" strokeWidth={2} />
            </LineChart>)}
          </ResponsiveContainer>
        </Card>

        {/* WEAK */}
        <Card title="Weak Areas" icon={AlertTriangle}>
          {weak.length ? (
            weak.map((w, i) => <p key={i}>• {w}</p>)
          ) : (
            "None 🎉"
          )}
        </Card>

        {/* INSIGHTS */}
        <Card title="Insights" icon={TrendingUp}>
          {insights.map((i, idx) => (
            <p key={idx}>• {i}</p>
          ))}
        </Card>

        {/* USER */}
        <Card title="Student" icon={User}>
          <p>{user?.name || "N/A"}</p>
          <p>{user?.email || ""}</p>
        </Card>

        {/* ACTION */}
        <div className="text-center">
          <button
            onClick={() => navigate(`/courses/${slug}/learn`)}
            className="bg-blue-600 text-white px-6 py-2 rounded flex items-center gap-2 mx-auto"
          >
            <RotateCcw className="w-4 h-4" />
            Retake Course
          </button>
        </div>

      </div>
    </div>
  );
}

/* ================= UI ================= */

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white p-4 rounded shadow text-center">
      <Icon className="mx-auto mb-2 text-blue-600" />
      <p className="text-sm">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white p-6 rounded shadow">
      <h2 className="flex items-center gap-2 font-semibold mb-4">
        <Icon size={18} className="text-blue-600" />
        {title}
      </h2>
      {children}
    </div>
  );
}